import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { toast } from "react-toastify";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Play,
  Pause,
  XCircle,
  Search,
  RefreshCw,
  CheckCheck,
  Upload,
  Megaphone,
  Settings2,
  ExternalLink,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  Ban,
  Building2,
  ChevronLeft,
  ChevronRight,
  Info,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/table";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import usePermissions from "../../hooks/usePermissions";

const FORM_VAZIO = {
  name: "",
  segment: "",
  country: "Brasil",
  state: "",
  city: "",
  district: "",
  audienceDescription: "",
  keywords: "",
  maxResults: 50,
  source: "osm",
  criteria: {}
};

const STATUS_PESQUISA = {
  draft: { label: "Rascunho", variant: "secondary" },
  queued: { label: "Na fila", variant: "outline" },
  running: { label: "Em andamento", variant: "default" },
  paused: { label: "Pausada", variant: "secondary" },
  completed: { label: "Concluída", variant: "default" },
  partial: { label: "Parcial", variant: "secondary" },
  error: { label: "Erro", variant: "destructive" },
  cancelled: { label: "Cancelada", variant: "outline" }
};

const STATUS_VERIFICACAO = {
  nao_verificado: { label: "Não verificado", variant: "outline" },
  numero_identificado: { label: "Número identificado", variant: "secondary" },
  numero_invalido: { label: "Número inválido", variant: "destructive" },
  whatsapp_confirmado: { label: "WhatsApp confirmado", variant: "default" },
  whatsapp_nao_identificado: { label: "Sem WhatsApp", variant: "secondary" },
  verificacao_indisponivel: { label: "Verificação indisponível", variant: "outline" },
  verificacao_erro: { label: "Erro na verificação", variant: "destructive" }
};

const STATUS_LEAD = {
  novo: "Novo",
  relevante: "Relevante",
  irrelevante: "Irrelevante",
  descartado: "Descartado",
  importado: "Importado"
};

const emAndamento = (status) => ["queued", "running"].includes(status);

const Prospeccao = () => {
  const { temPermissao } = usePermissions();

  const [buscas, setBuscas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [buscaTexto, setBuscaTexto] = useState("");
  const [selecionada, setSelecionada] = useState(null);

  const [resumo, setResumo] = useState(null);
  const [resultados, setResultados] = useState([]);
  const [carregandoResultados, setCarregandoResultados] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [temMais, setTemMais] = useState(false);
  const [filtros, setFiltros] = useState({ searchParam: "", situacao: "" });

  const [selecionados, setSelecionados] = useState([]);

  const [modalForm, setModalForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [sugerindo, setSugerindo] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const [fontes, setFontes] = useState([]);
  const [ajustes, setAjustes] = useState(null);

  const [modalImport, setModalImport] = useState(false);
  const [listas, setListas] = useState([]);
  const [importForm, setImportForm] = useState({ contactListId: "", newListName: "" });
  const [importando, setImportando] = useState(false);

  const [modalCampanha, setModalCampanha] = useState(false);
  const [campanhas, setCampanhas] = useState([]);
  const [campanhaId, setCampanhaId] = useState("");
  const [encaminhando, setEncaminhando] = useState(false);

  const [verificando, setVerificando] = useState(false);

  const [detalhe, setDetalhe] = useState(null);
  const [iaResumo, setIaResumo] = useState(null);
  const [iaMensagem, setIaMensagem] = useState("");
  const [carregandoIa, setCarregandoIa] = useState(false);

  // O handler do socket é registrado uma vez e precisa enxergar a pesquisa
  // selecionada no momento do evento — daí o ref, atualizado a cada mudança.
  const selecionadaRef = useRef(null);

  useEffect(() => {
    selecionadaRef.current = selecionada;
  }, [selecionada]);

  const carregarBuscas = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get("/prospect/searches", {
        params: { searchParam: buscaTexto || undefined }
      });
      setBuscas(data.searches || []);
    } catch (err) {
      toastError(err);
    }
    setCarregando(false);
  }, [buscaTexto]);

  useEffect(() => {
    carregarBuscas();
  }, [carregarBuscas]);

  useEffect(() => {
    api
      .get("/prospect/sources")
      .then(({ data }) => setFontes(Array.isArray(data) ? data : []))
      .catch(() => setFontes([]));
  }, []);

  // Ajustes da conta: definem o que já vem preenchido numa pesquisa nova.
  // Falha aqui não bloqueia a tela — só deixa o formulário sem os padrões.
  useEffect(() => {
    api
      .get("/prospect/settings")
      .then(({ data }) => setAjustes(data))
      .catch(() => setAjustes(null));
  }, []);

  const carregarResumo = useCallback(async (searchId) => {
    try {
      const { data } = await api.get(`/prospect/searches/${searchId}/summary`);
      setResumo(data);
    } catch {
      setResumo(null);
    }
  }, []);

  const carregarResultados = useCallback(
    async (searchId, page = 1) => {
      setCarregandoResultados(true);
      try {
        const { data } = await api.get(`/prospect/searches/${searchId}/results`, {
          params: {
            pageNumber: page,
            searchParam: filtros.searchParam || undefined,
            leadStatus: filtros.situacao || undefined,
            importados: filtros.importados ? "true" : undefined,
            naoImportados: filtros.naoImportados ? "true" : undefined,
            whatsappConfirmado: filtros.whatsappConfirmado ? "true" : undefined,
            duplicados: filtros.duplicados ? "true" : undefined
          }
        });
        setResultados(data.results || []);
        setTemMais(Boolean(data.hasMore));
        setPagina(page);
      } catch (err) {
        toastError(err);
      }
      setCarregandoResultados(false);
    },
    [filtros]
  );

  const abrirPesquisa = useCallback(
    async (busca) => {
      setSelecionada(busca);
      setSelecionados([]);
      setFiltros({ searchParam: "", situacao: "" });
      await Promise.all([
        carregarResumo(busca.id),
        carregarResultados(busca.id, 1)
      ]);
    },
    [carregarResumo, carregarResultados]
  );

  // Progresso em tempo real: o worker avisa cada mudança de estado da pesquisa.
  useEffect(() => {
    const socket = openSocket();
    const handler = (data) => {
      if (data?.action !== "update" || !data?.search) return;

      const atualizada = data.search;

      setBuscas((atuais) =>
        atuais.map((b) => (b.id === atualizada.id ? { ...b, ...atualizada } : b))
      );

      const alvo = selecionadaRef.current;
      if (alvo && alvo.id === atualizada.id) {
        setSelecionada((atual) => ({ ...atual, ...atualizada }));

        if (!emAndamento(atualizada.status)) {
          carregarResumo(atualizada.id);
          carregarResultados(atualizada.id, 1);
        }
      }
    };

    socket.on("prospectSearch", handler);
    return () => {
      socket.off("prospectSearch", handler);
      socket.disconnect();
    };
  }, [carregarResumo, carregarResultados]);

  const abrirNova = () => {
    setEditando(null);
    // A fonte padrão é a primeira disponível na conta — hoje o Google Places
    // quando há chave, senão o OpenStreetMap.
    const fonteInicial = fontes.find((f) => f.disponivel)?.key || FORM_VAZIO.source;
    setForm({
      ...FORM_VAZIO,
      source: fonteInicial,
      country: ajustes?.paisPadrao || FORM_VAZIO.country,
      state: ajustes?.estadoPadrao || "",
      city: ajustes?.cidadePadrao || "",
      maxResults: ajustes?.limiteResultadosPorPesquisa || FORM_VAZIO.maxResults,
      // Critérios são escolha de quem cria a pesquisa — nunca vêm marcados.
      criteria: {}
    });
    setModalForm(true);
  };

  const abrirEdicao = (busca) => {
    setEditando(busca);
    setForm({
      name: busca.name || "",
      segment: busca.segment || "",
      country: busca.country || "Brasil",
      state: busca.state || "",
      city: busca.city || "",
      district: busca.district || "",
      audienceDescription: busca.audienceDescription || "",
      keywords: busca.keywords || "",
      maxResults: busca.maxResults || 50,
      source: busca.source || "osm",
      criteria: busca.criteria || {}
    });
    setModalForm(true);
  };

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      if (editando) {
        await api.put(`/prospect/searches/${editando.id}`, form);
        toast.success("Pesquisa salva.");
      } else {
        await api.post("/prospect/searches", form);
        toast.success("Pesquisa criada. Clique em iniciar para buscar as empresas.");
      }
      setModalForm(false);
      carregarBuscas();
    } catch (err) {
      toastError(err);
    }
    setSalvando(false);
  };

  const sugerirPalavras = async () => {
    setSugerindo(true);
    try {
      const { data } = await api.post("/prospect/ai/keywords", {
        audienceDescription: form.audienceDescription,
        segment: form.segment,
        city: form.city,
        state: form.state
      });
      const termos = (data.palavrasChave || []).join(", ");
      setForm((atual) => ({
        ...atual,
        keywords: termos || atual.keywords
      }));
      toast.success(
        data.observacao
          ? `Sugestões aplicadas. ${data.observacao}`
          : "Sugestões aplicadas nas palavras-chave."
      );
    } catch (err) {
      toastError(err);
    }
    setSugerindo(false);
  };

  const iniciar = async (busca) => {
    try {
      const { data } = await api.post(`/prospect/searches/${busca.id}/start`);
      setBuscas((atuais) => atuais.map((b) => (b.id === data.id ? data : b)));
      if (selecionada?.id === data.id) setSelecionada(data);
      toast.success("Pesquisa enviada para processamento.");
    } catch (err) {
      toastError(err);
    }
  };

  const pausar = async (busca) => {
    try {
      const { data } = await api.post(`/prospect/searches/${busca.id}/pause`);
      setBuscas((atuais) => atuais.map((b) => (b.id === data.id ? data : b)));
      if (selecionada?.id === data.id) setSelecionada(data);
      toast.success("Pesquisa pausada.");
    } catch (err) {
      toastError(err);
    }
  };

  const cancelar = async (busca) => {
    try {
      const { data } = await api.post(`/prospect/searches/${busca.id}/cancel`);
      setBuscas((atuais) => atuais.map((b) => (b.id === data.id ? data : b)));
      if (selecionada?.id === data.id) setSelecionada(data);
      toast.success("Pesquisa cancelada.");
    } catch (err) {
      toastError(err);
    }
  };

  const excluir = async () => {
    if (!selecionada) return;
    try {
      await api.delete(`/prospect/searches/${selecionada.id}`);
      toast.success("Pesquisa removida.");
      setSelecionada(null);
      setResultados([]);
      setResumo(null);
      carregarBuscas();
    } catch (err) {
      toastError(err);
    }
  };

  const verificarWhatsapp = async () => {
    if (!selecionada) return;
    setVerificando(true);
    try {
      const { data } = await api.post(`/prospect/searches/${selecionada.id}/verify`, {
        resultIds: selecionados.length ? selecionados : undefined,
        todos: selecionados.length ? undefined : true
      });

      if (data.indisponivel) {
        toast.warn(data.motivo);
      } else {
        toast.success(
          `Conferidos ${data.verificados}: ${data.confirmados} com WhatsApp, ` +
            `${data.naoIdentificados} sem WhatsApp, ${data.erros} com erro.`
        );
      }

      setSelecionados([]);
      carregarResumo(selecionada.id);
      carregarResultados(selecionada.id, pagina);
    } catch (err) {
      toastError(err);
    }
    setVerificando(false);
  };

  const abrirImport = async () => {
    setImportForm({ contactListId: "", newListName: "" });
    setModalImport(true);
    try {
      const { data } = await api.get("/contact-lists");
      setListas(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  };

  const confirmarImport = async () => {
    if (!selecionada) return;
    setImportando(true);
    try {
      const { data } = await api.post(`/prospect/searches/${selecionada.id}/import`, {
        resultIds: selecionados.length ? selecionados : undefined,
        todos: selecionados.length ? undefined : true,
        contactListId: importForm.contactListId || undefined,
        newListName: importForm.contactListId ? undefined : importForm.newListName
      });

      toast.success(
        `${data.importados} lead(s) importado(s) para "${data.contactListName}". ` +
          `${data.duplicadosNoCrm} já eram contatos do CRM` +
          (data.semTelefone ? `, ${data.semTelefone} sem telefone` : "") +
          "."
      );

      setModalImport(false);
      setSelecionados([]);
      carregarResumo(selecionada.id);
      carregarResultados(selecionada.id, pagina);
    } catch (err) {
      toastError(err);
    }
    setImportando(false);
  };

  const abrirCampanha = async () => {
    setCampanhaId("");
    setModalCampanha(true);
    try {
      const { data } = await api.get("/campaigns");
      const lista = Array.isArray(data) ? data : data.campaigns || [];
      setCampanhas(
        lista.filter((c) => !["completed", "cancelled", "running"].includes(c.status))
      );
    } catch (err) {
      toastError(err);
    }
  };

  const confirmarEncaminhar = async () => {
    if (!selecionada || !campanhaId) return;
    setEncaminhando(true);
    try {
      const { data } = await api.post(`/prospect/searches/${selecionada.id}/forward`, {
        campaignId: campanhaId,
        resultIds: selecionados.length ? selecionados : undefined,
        todos: selecionados.length ? undefined : true
      });

      toast.success(
        `${data.encaminhados} lead(s) encaminhado(s) para "${data.campaignName}". ` +
          `${data.inelegiveis} sem destino de WhatsApp, ${data.jaNaCampanha} já estavam na campanha.`
      );

      setModalCampanha(false);
      setSelecionados([]);
    } catch (err) {
      toastError(err);
    }
    setEncaminhando(false);
  };

  const marcarLead = async (resultado, leadStatus) => {
    try {
      const { data } = await api.put(`/prospect/results/${resultado.id}`, { leadStatus });
      setResultados((atuais) => atuais.map((r) => (r.id === data.id ? data : r)));
      toast.success("Lead atualizado.");
    } catch (err) {
      toastError(err);
    }
  };

  const resumirComIa = async (resultado) => {
    setCarregandoIa(true);
    setIaResumo(null);
    setIaMensagem("");
    try {
      const { data } = await api.post("/prospect/ai/lead-summary", {
        lead: {
          name: resultado.name,
          segment: resultado.segment,
          city: resultado.city,
          state: resultado.state,
          phone: resultado.phone,
          site: resultado.site,
          address: resultado.address,
          verificationStatus: resultado.verificationStatus
        }
      });
      setIaResumo(data);
    } catch (err) {
      toastError(err);
    }
    setCarregandoIa(false);
  };

  const gerarMensagem = async (resultado) => {
    setCarregandoIa(true);
    setIaMensagem("");
    try {
      const { data } = await api.post("/prospect/ai/lead-message", {
        lead: {
          name: resultado.name,
          segment: resultado.segment,
          city: resultado.city
        }
      });
      setIaMensagem(data.mensagem || "");
    } catch (err) {
      toastError(err);
    }
    setCarregandoIa(false);
  };

  const todosSelecionados = useMemo(
    () => resultados.length > 0 && resultados.every((r) => selecionados.includes(r.id)),
    [resultados, selecionados]
  );

  const alternarTodos = () => {
    setSelecionados(todosSelecionados ? [] : resultados.map((r) => r.id));
  };

  const alternarUm = (id) => {
    setSelecionados((atuais) =>
      atuais.includes(id) ? atuais.filter((i) => i !== id) : [...atuais, id]
    );
  };

  const podeCriar = temPermissao("prospeccao:create");
  const podeEditar = temPermissao("prospeccao:edit");
  const podeExcluir = temPermissao("prospeccao:delete");
  const podeImportar = temPermissao("prospeccao:import");

  const fonteDisponivel = fontes.find((f) => f.key === form.source);

  return (
    <MainContainer>
      <ConfirmationModal
        title={selecionada && `Remover a pesquisa "${selecionada.name}"?`}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={excluir}
        confirmLabel="Excluir pesquisa"
      >
        A pesquisa sai da lista. Os resultados e o histórico de importação são
        mantidos para rastreabilidade.
      </ConfirmationModal>

      <MainHeader>
        <Title>Prospecção</Title>
        <MainHeaderButtonsWrapper>
          <Button asChild variant="outline">
            <Link to="/prospect/config">
              <Settings2 className="h-4 w-4" />
              Config. de Prospecção
            </Link>
          </Button>
          {podeCriar && (
            <Button onClick={abrirNova}>
              <Plus className="h-4 w-4" />
              Nova pesquisa
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className="@container flex-1 overflow-hidden px-4 pb-4 sm:px-6 sm:pb-6">
        <div className="grid h-full grid-cols-1 gap-4 @[1100px]:grid-cols-[340px_1fr]">
          {/* Coluna das pesquisas */}
          <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border bg-card">
            <div className="border-b p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Buscar pesquisa"
                  value={buscaTexto}
                  onChange={(e) => setBuscaTexto(e.target.value)}
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {carregando && (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}

              {!carregando && buscas.length === 0 && (
                <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                  Nenhuma pesquisa ainda.
                  <span className="mt-1 block text-xs">
                    Crie uma pesquisa para encontrar empresas por segmento e
                    localidade.
                  </span>
                </div>
              )}

              {buscas.map((busca) => {
                const status = STATUS_PESQUISA[busca.status] || STATUS_PESQUISA.draft;
                const ativa = selecionada?.id === busca.id;

                return (
                  <button
                    key={busca.id}
                    type="button"
                    onClick={() => abrirPesquisa(busca)}
                    className={`mb-1 w-full rounded-lg border p-3 text-left transition-colors ${
                      ativa
                        ? "border-primary/40 bg-primary/5"
                        : "border-transparent hover:border-border hover:bg-accent/50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">
                        {busca.name}
                      </span>
                      <Badge variant={status.variant} className="shrink-0">
                        {status.label}
                      </Badge>
                    </div>

                    <p className="mt-1 truncate text-xs text-muted-foreground">
                      {[busca.segment, busca.city, busca.state]
                        .filter(Boolean)
                        .join(" · ") || "Sem segmento/localidade"}
                    </p>

                    {emAndamento(busca.status) && (
                      <div className="mt-2">
                        <Progress
                          value={
                            busca.maxResults
                              ? Math.min(
                                  100,
                                  Math.round(
                                    ((busca.progress || 0) / busca.maxResults) * 100
                                  )
                                )
                              : 0
                          }
                          status="indigo"
                        />
                      </div>
                    )}

                    {!emAndamento(busca.status) && busca.status !== "draft" && (
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        {busca.totalFound || 0} encontrada(s) ·{" "}
                        {busca.totalImported || 0} importada(s)
                      </p>
                    )}

                    {busca.status === "error" && busca.errorMessage && (
                      <p className="mt-1 line-clamp-2 text-xs text-destructive">
                        {busca.errorMessage}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Coluna dos resultados */}
          <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border bg-card">
            {!selecionada ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
                <Building2 className="h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm font-medium">Escolha uma pesquisa</p>
                <p className="max-w-sm text-xs text-muted-foreground">
                  Selecione uma pesquisa à esquerda para ver os resultados, conferir
                  os números e levar os leads para as listas e campanhas.
                </p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{selecionada.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {[selecionada.segment, selecionada.city, selecionada.state]
                        .filter(Boolean)
                        .join(" · ") || "Sem segmento/localidade"}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    {selecionada.status === "draft" && podeEditar && (
                      <Button size="sm" onClick={() => iniciar(selecionada)}>
                        <Play className="h-3.5 w-3.5" />
                        Iniciar
                      </Button>
                    )}

                    {selecionada.status === "paused" && podeEditar && (
                      <Button size="sm" onClick={() => iniciar(selecionada)}>
                        <Play className="h-3.5 w-3.5" />
                        Retomar
                      </Button>
                    )}

                    {selecionada.status === "running" && podeEditar && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => pausar(selecionada)}
                      >
                        <Pause className="h-3.5 w-3.5" />
                        Pausar
                      </Button>
                    )}

                    {["queued", "running", "paused"].includes(selecionada.status) &&
                      podeEditar && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => cancelar(selecionada)}
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          Cancelar
                        </Button>
                      )}

                    {["completed", "partial", "error", "cancelled"].includes(
                      selecionada.status
                    ) &&
                      podeEditar && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => iniciar(selecionada)}
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                          Atualizar resultados
                        </Button>
                      )}

                    {podeEditar && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => abrirEdicao(selecionada)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}

                    {podeExcluir && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setConfirmOpen(true)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Números do topo */}
                {resumo && (
                  <div className="grid grid-cols-3 gap-px border-b bg-border sm:grid-cols-6">
                    {[
                      ["Encontradas", resumo.total],
                      ["Com telefone", resumo.comTelefone],
                      ["WhatsApp", resumo.whatsappConfirmado],
                      ["No CRM", resumo.jaExistemNoCrm],
                      ["Importadas", resumo.importados],
                      ["Duplicadas", resumo.duplicados || 0]
                    ].map(([rotulo, valor]) => (
                      <div key={rotulo} className="bg-card px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          {rotulo}
                        </p>
                        <p className="text-lg font-semibold tabular-nums">{valor}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Filtros */}
                <div className="flex flex-wrap items-center gap-2 border-b p-3">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      className="h-8 w-52 pl-8 text-sm"
                      placeholder="Empresa, telefone ou cidade"
                      value={filtros.searchParam}
                      onChange={(e) =>
                        setFiltros((f) => ({ ...f, searchParam: e.target.value }))
                      }
                    />
                  </div>

                  <Select
                    value={filtros.situacao || "todas"}
                    onValueChange={(v) =>
                      setFiltros((f) => ({ ...f, situacao: v === "todas" ? "" : v }))
                    }
                  >
                    <SelectTrigger className="h-8 w-40 text-sm">
                      <SelectValue placeholder="Situação" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todas">Todas as situações</SelectItem>
                      {Object.entries(STATUS_LEAD).map(([valor, rotulo]) => (
                        <SelectItem key={valor} value={valor}>
                          {rotulo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {[
                    ["whatsappConfirmado", "WhatsApp confirmado"],
                    ["importados", "Já importados"],
                    ["naoImportados", "Não importados"],
                    ["duplicados", "Duplicados"]
                  ].map(([campo, rotulo]) => (
                    <label
                      key={campo}
                      className="flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs"
                    >
                      <Checkbox
                        checked={Boolean(filtros[campo])}
                        onCheckedChange={(v) =>
                          setFiltros((f) => ({ ...f, [campo]: Boolean(v) }))
                        }
                      />
                      {rotulo}
                    </label>
                  ))}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => carregarResultados(selecionada.id, 1)}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Aplicar
                  </Button>

                  <span className="ml-auto text-xs text-muted-foreground">
                    {selecionados.length
                      ? `${selecionados.length} selecionado(s)`
                      : `${resultados.length} nesta página`}
                  </span>
                </div>

                {/* Ações em lote */}
                <div className="flex flex-wrap items-center gap-1.5 border-b bg-muted/30 px-3 py-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={verificando}
                    onClick={verificarWhatsapp}
                  >
                    {verificando ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <CheckCheck className="h-3.5 w-3.5" />
                    )}
                    Conferir WhatsApp
                  </Button>

                  {podeImportar && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!resumo?.total}
                        onClick={abrirImport}
                      >
                        <Upload className="h-3.5 w-3.5" />
                        Importar para lista
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!resumo?.total}
                        onClick={abrirCampanha}
                      >
                        <Megaphone className="h-3.5 w-3.5" />
                        Encaminhar para campanha
                      </Button>
                    </>
                  )}

                  <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                    <Info className="h-3.5 w-3.5" />
                    Sem seleção, a ação vale para todos os resultados.
                  </span>
                </div>

                {/* Tabela */}
                <div className="min-h-0 flex-1 overflow-auto">
                  <Table className="table-fixed">
                    <TableHeader className="sticky top-0 z-10 bg-card">
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={todosSelecionados}
                            onCheckedChange={alternarTodos}
                            aria-label="Selecionar todos"
                          />
                        </TableHead>
                        <TableHead className="w-[26%]">Empresa</TableHead>
                        <TableHead className="w-[14%]">Segmento</TableHead>
                        <TableHead className="w-[12%]">Cidade/UF</TableHead>
                        <TableHead className="w-[13%]">Telefone</TableHead>
                        <TableHead className="w-[14%]">WhatsApp</TableHead>
                        <TableHead className="w-[8%]">Site</TableHead>
                        <TableHead className="w-[13%] text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {resultados.map((r) => {
                        const verif =
                          STATUS_VERIFICACAO[r.verificationStatus] ||
                          STATUS_VERIFICACAO.nao_verificado;

                        return (
                          <TableRow key={r.id}>
                            <TableCell>
                              <Checkbox
                                checked={selecionados.includes(r.id)}
                                onCheckedChange={() => alternarUm(r.id)}
                                aria-label={`Selecionar ${r.name}`}
                              />
                            </TableCell>

                            <TableCell className="font-medium">
                              <button
                                type="button"
                                className="block max-w-full truncate text-left hover:underline"
                                onClick={() => {
                                  setDetalhe(r);
                                  setIaResumo(null);
                                  setIaMensagem("");
                                }}
                                title={r.name}
                              >
                                {r.name}
                              </button>
                              {r.existingContactId && (
                                <span className="text-[11px] text-muted-foreground">
                                  Já está no CRM
                                </span>
                              )}
                              {r.leadStatus !== "novo" && (
                                <Badge variant="secondary" className="mt-0.5 text-[10px]">
                                  {STATUS_LEAD[r.leadStatus]}
                                </Badge>
                              )}
                            </TableCell>

                            <TableCell className="truncate text-muted-foreground">
                              {r.segment || "—"}
                            </TableCell>

                            <TableCell className="truncate text-muted-foreground">
                              {[r.city, r.state].filter(Boolean).join("/") || "—"}
                            </TableCell>

                            <TableCell className="tabular-nums text-muted-foreground">
                              {r.phone || "—"}
                            </TableCell>

                            <TableCell>
                              <Badge variant={verif.variant}>{verif.label}</Badge>
                            </TableCell>

                            <TableCell>
                              {r.site ? (
                                <a
                                  href={`https://${String(r.site).replace(/^https?:\/\//, "")}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-primary hover:underline"
                                  title={r.site}
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>

                            <TableCell>
                              <div className="flex items-center justify-end gap-0.5">
                                {r.sourceUrl && (
                                  <Button
                                    asChild
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    title="Consultar a fonte"
                                  >
                                    <a href={r.sourceUrl} target="_blank" rel="noreferrer">
                                      <ExternalLink className="h-3.5 w-3.5" />
                                    </a>
                                  </Button>
                                )}
                                {podeEditar && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      title="Marcar como relevante"
                                      onClick={() => marcarLead(r, "relevante")}
                                    >
                                      <ThumbsUp className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      title="Marcar como irrelevante"
                                      onClick={() => marcarLead(r, "irrelevante")}
                                    >
                                      <ThumbsDown className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-destructive hover:text-destructive"
                                      title="Descartar"
                                      onClick={() => marcarLead(r, "descartado")}
                                    >
                                      <Ban className="h-3.5 w-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {carregandoResultados && <TableRowSkeleton columns={7} />}

                      {!carregandoResultados && resultados.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            className="py-10 text-center text-sm text-muted-foreground"
                          >
                            {emAndamento(selecionada.status)
                              ? "Buscando empresas… os resultados aparecem aqui sozinhos."
                              : "Nenhum resultado com os filtros atuais."}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Paginação */}
                <div className="flex items-center justify-between border-t px-3 py-2">
                  <span className="text-xs text-muted-foreground">
                    Página {pagina}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagina <= 1}
                      onClick={() => carregarResultados(selecionada.id, pagina - 1)}
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!temMais}
                      onClick={() => carregarResultados(selecionada.id, pagina + 1)}
                    >
                      Próxima
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal: criar/editar pesquisa */}
      <Dialog open={modalForm} onOpenChange={setModalForm}>
        <DialogContent className="sm:max-w-3xl">
          <form onSubmit={salvar}>
            <DialogHeader>
              <DialogTitle>{editando ? "Editar pesquisa" : "Nova pesquisa"}</DialogTitle>
              <DialogDescription>
                Informe o segmento e a localidade. A cidade (ou ao menos o estado) é
                obrigatória — sem um recorte, a busca perde sentido.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label htmlFor="name">Nome da pesquisa</Label>
                  <Input
                    id="name"
                    required
                    placeholder="Ex.: Empresas de informática em Guarulhos"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="segment">Segmento ou tipo de empresa</Label>
                  <Input
                    id="segment"
                    placeholder="Ex.: informática, contabilidade, clínicas"
                    value={form.segment}
                    onChange={(e) => setForm({ ...form, segment: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="source">Fonte de pesquisa</Label>
                  <Select
                    value={form.source}
                    onValueChange={(v) => setForm({ ...form, source: v })}
                  >
                    <SelectTrigger id="source">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {fontes.map((f) => (
                        <SelectItem key={f.key} value={f.key} disabled={!f.disponivel}>
                          {f.nome}
                          {!f.disponivel ? " (indisponível)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fonteDisponivel && !fonteDisponivel.disponivel && (
                    <p className="mt-1 text-xs text-destructive">
                      {fonteDisponivel.motivo}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="country">País</Label>
                  <Input
                    id="country"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="state">Estado</Label>
                    <Input
                      id="state"
                      placeholder="Ex.: SP"
                      value={form.state}
                      onChange={(e) => setForm({ ...form, state: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">Cidade</Label>
                    <Input
                      id="city"
                      placeholder="Ex.: Guarulhos"
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="district">Região ou bairro</Label>
                  <Input
                    id="district"
                    placeholder="Opcional"
                    value={form.district}
                    onChange={(e) => setForm({ ...form, district: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="maxResults">Quantidade máxima de resultados</Label>
                  <Input
                    id="maxResults"
                    type="number"
                    min="1"
                    max={ajustes?.limiteResultadosPorPesquisa || 1000}
                    value={form.maxResults}
                    onChange={(e) => setForm({ ...form, maxResults: e.target.value })}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    A conta libera até{" "}
                    {ajustes?.limiteResultadosPorPesquisa || 1000} resultados por
                    pesquisa; um valor maior é cortado nesse teto.
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="audienceDescription">Descrição do público desejado</Label>
                <Textarea
                  id="audienceDescription"
                  rows={3}
                  placeholder="Ex.: pequenas e médias empresas que possam precisar de suporte técnico e manutenção de computadores."
                  value={form.audienceDescription}
                  onChange={(e) =>
                    setForm({ ...form, audienceDescription: e.target.value })
                  }
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="keywords">Palavras-chave</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={sugerindo}
                    onClick={sugerirPalavras}
                  >
                    {sugerindo ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}
                    Sugerir com IA
                  </Button>
                </div>
                <Textarea
                  id="keywords"
                  rows={2}
                  placeholder="Termos adicionais, separados por vírgula"
                  value={form.keywords}
                  onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                />
              </div>

              <div>
                <Label>Critérios de pesquisa</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  {[
                    ["comSite", "Empresas com site"],
                    ["semSite", "Empresas sem site identificado"],
                    ["comTelefone", "Empresas com telefone"],
                    ["comWhatsapp", "Empresas com WhatsApp identificável"]
                  ].map(([chave, rotulo]) => (
                    <label
                      key={chave}
                      className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm"
                    >
                      <Checkbox
                        checked={Boolean(form.criteria?.[chave])}
                        onCheckedChange={(v) =>
                          setForm({
                            ...form,
                            criteria: { ...form.criteria, [chave]: Boolean(v) }
                          })
                        }
                      />
                      {rotulo}
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  A ausência de site não é prova de que a empresa não tem site. Um
                  telefone encontrado também não significa WhatsApp confirmado — a
                  confirmação é feita em etapa separada.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setModalForm(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={salvando}>
                {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
                Salvar pesquisa
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal: importar para lista */}
      <Dialog open={modalImport} onOpenChange={setModalImport}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Importar para Lista de Contatos</DialogTitle>
            <DialogDescription>
              {selecionados.length
                ? `${selecionados.length} lead(s) selecionado(s).`
                : "Todos os resultados desta pesquisa serão considerados."}{" "}
              Leads sem telefone utilizável são ignorados.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div>
              <Label htmlFor="listaExistente">Lista existente</Label>
              <Select
                value={importForm.contactListId || "nova"}
                onValueChange={(v) =>
                  setImportForm({
                    contactListId: v === "nova" ? "" : v,
                    newListName: ""
                  })
                }
              >
                <SelectTrigger id="listaExistente">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nova">Criar uma nova lista</SelectItem>
                  {listas.map((l) => (
                    <SelectItem key={l.id} value={String(l.id)}>
                      {l.name} ({(l.items || []).length} contato(s))
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!importForm.contactListId && (
              <div>
                <Label htmlFor="newListName">Nome da nova lista</Label>
                <Input
                  id="newListName"
                  placeholder="Ex.: Leads informática Guarulhos"
                  value={importForm.newListName}
                  onChange={(e) =>
                    setImportForm((f) => ({ ...f, newListName: e.target.value }))
                  }
                />
              </div>
            )}

            <p className="flex gap-1.5 rounded-md bg-muted p-3 text-xs leading-relaxed">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Contatos que já existem no CRM não são sobrescritos: eles apenas
              ficam referenciados no lead.
            </p>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalImport(false)}>
              Cancelar
            </Button>
            <Button
              onClick={confirmarImport}
              disabled={
                importando ||
                (!importForm.contactListId && importForm.newListName.trim().length < 2)
              }
            >
              {importando && <Loader2 className="h-4 w-4 animate-spin" />}
              Importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: encaminhar para campanha */}
      <Dialog open={modalCampanha} onOpenChange={setModalCampanha}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Encaminhar para campanha</DialogTitle>
            <DialogDescription>
              Os leads viram destinatários da campanha escolhida. A campanha
              continua responsável pelo disparo, com as regras dela.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <Label htmlFor="campanha">Campanha</Label>
            <Select value={campanhaId} onValueChange={setCampanhaId}>
              <SelectTrigger id="campanha">
                <SelectValue placeholder="Escolha a campanha" />
              </SelectTrigger>
              <SelectContent>
                {campanhas.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {campanhas.length === 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                Nenhuma campanha disponível para receber contatos agora.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalCampanha(false)}>
              Cancelar
            </Button>
            <Button onClick={confirmarEncaminhar} disabled={encaminhando || !campanhaId}>
              {encaminhando && <Loader2 className="h-4 w-4 animate-spin" />}
              Encaminhar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: detalhe do lead */}
      <Dialog open={Boolean(detalhe)} onOpenChange={(v) => !v && setDetalhe(null)}>
        <DialogContent className="sm:max-w-2xl">
          {detalhe && (
            <>
              <DialogHeader>
                <DialogTitle>{detalhe.name}</DialogTitle>
                <DialogDescription>
                  {[detalhe.segment, detalhe.city, detalhe.state]
                    .filter(Boolean)
                    .join(" · ") || "Sem segmento/localidade"}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-3 py-2 text-sm sm:grid-cols-2">
                {[
                  ["Telefone", detalhe.phone],
                  ["Número normalizado", detalhe.normalizedPhone],
                  ["Site", detalhe.site],
                  ["Endereço", detalhe.address],
                  ["Fonte", detalhe.source],
                  [
                    "Verificação",
                    (STATUS_VERIFICACAO[detalhe.verificationStatus] || {}).label
                  ],
                  ["Situação do lead", STATUS_LEAD[detalhe.leadStatus]],
                  ["Coletado em", detalhe.sourceFetchedAt ? new Date(detalhe.sourceFetchedAt).toLocaleString("pt-BR") : null]
                ].map(([rotulo, valor]) => (
                  <div key={rotulo}>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      {rotulo}
                    </p>
                    <p className="truncate">{valor || "não informado"}</p>
                  </div>
                ))}
              </div>

              <div className="border-t pt-3">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={carregandoIa}
                    onClick={() => resumirComIa(detalhe)}
                  >
                    {carregandoIa ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}
                    Resumir com IA
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={carregandoIa}
                    onClick={() => gerarMensagem(detalhe)}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Sugerir mensagem
                  </Button>
                </div>

                {iaResumo && (
                  <div className="mt-3 space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
                    <p>{iaResumo.resumo}</p>
                    {iaResumo.pendencias?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Precisa de conferência
                        </p>
                        <ul className="ml-4 list-disc text-xs">
                          {iaResumo.pendencias.map((p, i) => (
                            <li key={i}>{p}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Texto gerado por IA para conferência. Não é dado confirmado.
                    </p>
                  </div>
                )}

                {iaMensagem && (
                  <div className="mt-3 rounded-md border bg-muted/30 p-3 text-sm">
                    <Textarea rows={5} value={iaMensagem} readOnly />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Rascunho gerado por IA. Revise antes de usar — nada é enviado
                      por aqui.
                    </p>
                  </div>
                )}
              </div>

              <DialogFooter>
                {detalhe.sourceUrl && (
                  <Button asChild variant="outline">
                    <a href={detalhe.sourceUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-4 w-4" />
                      Consultar a fonte
                    </a>
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setDetalhe(null)}>
                  Fechar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </MainContainer>
  );
};

export default Prospeccao;
